import {
	CAR_INSURANCE_FAQS,
	CAR_INSURANCE_HEADER_IMAGE,
	CAR_INSURANCE_SEO,
	INSURANCE_PROVIDERS,
} from "@/data/consts";
import Image from "next/image";
import * as React from "react";
import FAQs from "@/components/FAQs";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import { getOne } from "@/api/contentful";
import { Providers } from "@/types/providers";
import { createDealClick } from "@/api/tracking";
import { UtmContext } from "@/context/utmContext";
import PageSEO from "@/components/Shared/PageSEO";
import { KeywordContext } from "@/context/keyword";
import { FAQS, PageSEO as PageSEOType } from "@/types/index";
import { HeaderImage as HeaderImageType } from "@/types/header";
import InsuranceCards from "@/components/Insurance/InsuranceCardList";
import InsuranceBenefits from "@/components/Insurance/InsuranceBenefits";
import WeWorkWithInsurance from "@/components/Insurance/WeWorkWithInsurance";
import InsuranceHeaderContent from "@/components/Header/InsuranceHeaderContent";

const Breadcrumbs = [
	{
		title: "Home",
		href: "/",
		active: false,
	},
	{
		title: "Insurance",
		href: "/insurance",
		active: false,
	},
	{
		title: "Car Insurance",
		href: "/car-insurance",
		active: true,
	},
];

const headerContentListItems = [
	"Compare quotes from 110+ UK insurance providers",
	"You could save up to £523*",
	"See if you can find a better car insurance deal",
];

const insuranceBenefitsListItems = [
	{
		heading: "Time",
		text: "Quickly and easily compare insurance in one place",
	},
	{
		heading: "110+",
		text: "Get access to over 110 insurance providers.",
	},

	{
		heading: "Free",
		text: "The comparison site is completely free to use.",
	},
];

const insuranceHref =
	"https://insurance.savely.co.uk/car/index.php?id=64394a5915073aeda43a2395977b1405";

const CarInsurance: React.FC<{
	pageSEO: PageSEOType;
	headerImage: HeaderImageType;
	FAQsContent: FAQS;
	providers: {
		fields: {
			providerLogos: Array<Providers>;
		};
	};
}> = ({ pageSEO, headerImage, FAQsContent, providers }) => {
	const { utmContent, utmCampaign, utmMedium, utmSource, utmTerm } =
		React.useContext(UtmContext);
	const { sclid, gclid, fbclid, msclkid, tbclid, obclid } =
		React.useContext(KeywordContext);

	const onDealClick = async () => {
		createDealClick({
			type: "car-insurance",
			click_reference:
				sclid ?? gclid ?? fbclid ?? msclkid ?? tbclid ?? obclid ?? utmContent,
			utm_campaign: utmCampaign,
			utm_content: utmContent,
			utm_source: utmSource,
			utm_medium: utmMedium,
			utm_term: utmTerm,
			product: "Car Insurance",
			provider: "N/A",
			metadata: {},
		});
	};

	function generateInsuranceHref(): string {
		let href = insuranceHref;
		if (sclid || gclid || fbclid || msclkid || tbclid || obclid || utmContent) {
			href +=
				"&kw=" +
				(sclid || gclid || fbclid || msclkid || tbclid || obclid || utmContent);
		}
		return href;
	}

	return (
		<div className="h-100 d-flex flex-column">
			<PageSEO
				title={pageSEO.fields.pageTitle}
				description={pageSEO.fields.metaDescription}
				keywords={pageSEO.fields.metaKeywords}
			/>
			<Header
				left={
					<InsuranceHeaderContent
						title={"Compare Car Insurance Quotes"}
						listItems={headerContentListItems}
						insuranceLink={generateInsuranceHref()}
						vehicleReg={true}
						onClick={onDealClick}
					/>
				}
				right={
					<Image
						src={"https:" + headerImage.fields.image?.fields.file?.url}
						alt={headerImage.fields.image?.fields.title ?? ""}
						height="350"
						width="600"
					/>
				}
				theme={{
					paddingTop: 5,
					paddingRight: 0,
					paddingLeft: 0,
					paddingBottom: 0,
				}}
				breadcrumbs={Breadcrumbs}
			/>
			<InsuranceBenefits
				headingTitle={"Looking for car insurance?"}
				benefits={insuranceBenefitsListItems}
			/>
			<WeWorkWithInsurance providers={providers.fields.providerLogos} />
			<InsuranceCards title={"Related Insurance"} />
			<FAQs data={FAQsContent} />
			<section className={` pt-5 pb-5 text-center bg-blue`}>
				<div className="container-fluid pt-5 pb-5">
					<InsuranceHeaderContent
						title={"Compare Car Insurance Quotes"}
						listItems={headerContentListItems}
						insuranceLink={
							insuranceHref +
							(sclid ||
							gclid ||
							fbclid ||
							msclkid ||
							tbclid ||
							obclid ||
							utmContent
								? "&kw=" +
								  (sclid ||
										gclid ||
										fbclid ||
										msclkid ||
										tbclid ||
										obclid ||
										utmContent)
								: "")
						}
						disclaimerText={`*51% of consumers could save £523.47 on their Car Insurance. The saving was calculated by
comparing the cheapest price found with the average of the next six cheapest prices quoted
by insurance providers on Seopa Ltd’s insurance comparison website. This is based on
representative cost savings from August 2024 data. The savings you could achieve are
dependent on your individual circumstances and how you selected your current insurance
supplier.`}
						bottomHeader={true}
						onClick={onDealClick}
					/>
				</div>
			</section>
			<Footer />
		</div>
	);
};

export async function getStaticProps() {
	const pageSEO = await getOne(CAR_INSURANCE_SEO);
	const providers = await getOne(INSURANCE_PROVIDERS);
	const FAQsContent = await getOne(CAR_INSURANCE_FAQS);
	const headerImage = await getOne(CAR_INSURANCE_HEADER_IMAGE);

	return {
		props: {
			pageSEO,
			headerImage,
			FAQsContent,
			providers,
		},
		revalidate: Number(process.env.REVALIDATE_CONTENTFUL),
	};
}

export default CarInsurance;
